import { Link } from 'react-router-dom'
import { getBackdropUrl } from '../../lib/backdropUrl'
import { formatStartDateShort, formatRunsUntil } from '../../lib/leagueDate'
import DraftStartsIn from './DraftStartsIn'

const FORMAT_LABELS = {
  pickem: "Pick'em",
  survivor: 'Survivor',
  squares: 'Squares',
  bracket: 'Bracket',
  fantasy: 'Fantasy Football',
  nba_dfs: 'NBA Daily Fantasy',
  wnba_dfs: 'WNBA Daily Fantasy',
  mlb_dfs: 'MLB Daily Fantasy',
  hr_derby: 'Home Run Derby',
  strikeouts: 'Strikeouts Contest',
  three_point: 'NBA 3-Point Contest',
  wnba_three_point: 'WNBA 3-Point Contest',
  sacks: 'Sacks Contest',
  ints: 'Interceptions Contest',
  tackles: 'Tackles Contest',
  receptions: 'Receptions Contest',
  td_pass: 'TD Pass Competition',
}

const SPORT_LABELS = {
  americanfootball_nfl: 'NFL',
  basketball_nba: 'NBA',
  baseball_mlb: 'MLB',
  basketball_ncaab: 'NCAAB',
  basketball_wncaab: 'WNCAAB',
  americanfootball_ncaaf: 'NCAAF',
  basketball_wnba: 'WNBA',
  icehockey_nhl: 'NHL',
  soccer_usa_mls: 'MLS',
  soccer_world_cup: 'Int\'l Soccer',
  americanfootball_ufl: 'UFL',
  all: 'All Sports',
}

const STATUS_STYLES = {
  open: 'text-correct',
  active: 'text-correct',
  completed: 'text-text-muted',
  archived: 'text-text-muted',
}

function ordinal(n) {
  const v = n % 100
  if (v >= 11 && v <= 13) return `${n}th`
  const last = n % 10
  return `${n}${last === 1 ? 'st' : last === 2 ? 'nd' : last === 3 ? 'rd' : 'th'}`
}

function rankColor(rank) {
  if (rank === 1) return 'text-yellow-500'
  if (rank === 2) return 'text-slate-300'
  if (rank === 3) return 'text-amber-600'
  return 'text-text-primary'
}

export default function LeagueCard({ league, noLink }) {
  const hasBackdrop = !!league.backdrop_image
  const Wrapper = noLink ? 'div' : Link
  const wrapperProps = noLink ? {} : { to: `/leagues/${league.id}` }

  return (
    <Wrapper
      {...wrapperProps}
      className="block relative bg-bg-primary rounded-xl border border-text-primary/20 overflow-hidden hover:bg-text-primary/5 transition-colors"
    >
      {/* Readiness corner clip — tiny color flag with hover popover */}
      {league.readiness && !league.survivor_eliminated && (
        <div className="absolute top-0 right-0 z-20 group">
          <span
            className={`block w-3 h-3 rounded-bl-md ${
              league.readiness === 'ready'
                ? 'bg-correct'
                : league.readiness === 'attention'
                ? 'bg-yellow-500'
                : 'bg-incorrect'
            }`}
          />
          {/* Hover/touch popover — positioned up-and-left of the flag so it
              doesn't overlap the league's status text underneath. Solid
              bg-bg-primary (not translucent) so nothing underneath bleeds
              through the tooltip. */}
          <div className="hidden group-hover:block group-active:block absolute top-3 right-5 z-30 pointer-events-none">
            <div className={`whitespace-nowrap text-[11px] font-semibold px-2.5 py-1.5 rounded-lg shadow-lg border bg-bg-primary ${
              league.readiness === 'ready'
                ? 'border-correct/60 text-correct'
                : league.readiness === 'attention'
                ? 'border-yellow-500/60 text-yellow-500'
                : 'border-incorrect/60 text-incorrect'
            }`}>
              {league.readiness_detail || (
                league.readiness === 'ready' ? 'Ready for the next contest'
                : league.readiness === 'attention' ? 'Needs attention'
                : 'Action needed'
              )}
            </div>
          </div>
        </div>
      )}
      {hasBackdrop && (
        <>
          <img
            src={getBackdropUrl(league.backdrop_image)}
            alt=""
            className="absolute inset-0 w-full h-full object-cover opacity-30 pointer-events-none"
            style={{ objectPosition: `center ${league.backdrop_y ?? 50}%` }}
          />
          <div className="absolute inset-0 bg-bg-primary/50 pointer-events-none" />
        </>
      )}
      <div className="relative z-10 p-5">
        {/* Draft countdown — center-right of the card body, vertically
            centered. Doesn't affect layout flow because it's absolutely
            positioned. */}
        {league.format === 'fantasy' && league.draft_date && league.draft_status !== 'completed' && (
          <div className="hidden sm:block absolute right-5 bottom-4 text-right pointer-events-none">
            <DraftStartsIn draftDate={league.draft_date} draftStatus={league.draft_status} compact={false} />
          </div>
        )}
        <div className="flex items-start justify-between gap-3 mb-2">
          <h3 className="font-display text-lg text-white break-words">{league.name}</h3>
          {!league.survivor_eliminated && (
            <span className={`text-xs font-semibold px-2 py-0.5 rounded shrink-0 mt-1 ${STATUS_STYLES[league.status]}`}>
              {league.status}
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-text-muted min-h-[3.25rem] sm:min-h-0 content-start">
          <span className="font-semibold text-accent">
            {/* Touchdown survivor is a different game from team survivor —
                pick a PLAYER to score vs a TEAM to win — so it gets its own
                label rather than reading as a standard Survivor pool.
                HomePage, Open Leagues and Join already distinguish it; this
                shared card was the one surface still flattening both. */}
            {/* Salary cap is a materially different game from a traditional
                draft league — weekly lineup under a cap vs a season-long
                roster — so it gets named on the card rather than reading as
                plain Fantasy Football. Open Leagues already did this; this
                shared card is what My Leagues renders, so it was the surface
                still flattening the two. */}
            {league.format === 'survivor' && league.settings?.survivor_mode === 'touchdown'
              ? 'TD Survivor'
              : league.format === 'fantasy' && league.fantasy_format === 'salary_cap'
                ? 'Salary Cap Fantasy Football'
                : FORMAT_LABELS[league.format]}
          </span>
          {!['nba_dfs', 'wnba_dfs', 'mlb_dfs'].includes(league.format) && (
            <span>{SPORT_LABELS[league.sport]}</span>
          )}
          {league.format === 'bracket' && league.bracket_submitted_count != null ? (
            <span>{league.bracket_submitted_count} {league.bracket_submitted_count === 1 ? 'entry' : 'entries'}</span>
          ) : (
            <span>{league.member_count} {league.member_count === 1 ? 'member' : 'members'}</span>
          )}
          {league.my_role === 'commissioner' && (
            <span className="font-semibold text-tier-hof">Commish</span>
          )}
          {league.format === 'fantasy' && league.my_fantasy_rank != null && (
            <span className={`font-semibold ${rankColor(league.my_fantasy_rank)}`}>
              {ordinal(league.my_fantasy_rank)}
              {league.fantasy_total_teams ? ` of ${league.fantasy_total_teams}` : ''}
            </span>
          )}
        </div>
        {(() => {
          // Hide for fantasy leagues with a draft countdown — that block owns the bottom row.
          if (league.format === 'fantasy' && league.draft_date) return null
          const start = formatStartDateShort(league.starts_at)
          const end = formatRunsUntil(league)
          const notStartedYet = league.starts_at && new Date(league.starts_at) > new Date()
          if (!start && !end) return null
          // A bracket gets one clause, never a range: "Runs Sep 30 – Through
          // the playoffs" reads as a bug. It also has no honest end date —
          // a best-of-seven can finish four days apart.
          if (league.format === 'bracket') {
            return (
              <div className="text-xs text-text-muted mt-1.5">
                Runs <span className="text-text-secondary font-medium">through the playoffs</span>
              </div>
            )
          }
          // Pre-start: show the full range so users see exactly when it begins and ends.
          if (notStartedYet && start && end) {
            return (
              <div className="text-xs text-text-muted mt-1.5">
                Runs <span className="text-yellow-500 font-semibold">{start}</span>
                {' – '}
                <span className="text-text-secondary font-medium">{end}</span>
              </div>
            )
          }
          // Pre-start with no known end → "Starts <date>".
          if (notStartedYet && start) {
            return (
              <div className="text-xs text-text-muted mt-1.5">
                Starts <span className="text-yellow-500 font-semibold">{start}</span>
              </div>
            )
          }
          // Already underway → just the end label.
          if (end) {
            return (
              <div className="text-xs text-text-muted mt-1.5">
                Runs until <span className="text-text-secondary font-medium">{end}</span>
              </div>
            )
          }
          return null
        })()}
        {/* Mobile: countdown on its own line at the bottom since horizontal
            space is tight */}
        {league.format === 'fantasy' && league.draft_date && league.draft_status !== 'completed' && (
          <div className="sm:hidden mt-2">
            <DraftStartsIn draftDate={league.draft_date} draftStatus={league.draft_status} />
          </div>
        )}
        {league.format === 'survivor' && league.status === 'active' && league.survivor_alive != null && (
          <div className="absolute right-5 bottom-4 text-right pointer-events-none">
            <span className="text-sm text-correct font-semibold">{league.survivor_alive} still alive</span>
          </div>
        )}
      </div>
    </Wrapper>
  )
}
